import {
  lerp,
  lerpHeading,
  offsetByHeading,
  type LatLng,
} from './locationSmoothing';

export { lerpHeading };

export interface CameraState {
  lat: number;
  lng: number;
  heading: number;
  tilt: number;
  zoom: number;
}

export interface FollowCameraOptions {
  speedMps?: number;
  distanceToTurnM?: number | null;
  arrived?: boolean;
  /** Poor GPS → slower camera lerp */
  accuracyM?: number | null;
  isMoving?: boolean;
  /** Look-ahead along heading (meters). Default 20. */
  lookAheadM?: number;
}

/** Padding keeps rider near bottom third (above trip sheet, below turn banner). */
export function getNavigationMapPadding() {
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  return {
    top: Math.max(140, Math.round(h * 0.16)),
    right: 20,
    bottom: Math.round(h * 0.32),
    left: 20,
  };
}

export function createCameraState(center: LatLng): CameraState {
  return {
    lat: center.lat,
    lng: center.lng,
    heading: 0,
    tilt: 45,
    zoom: 17.2,
  };
}

/** Target zoom from speed + upcoming turn proximity. */
export function computeNavZoom(opts: FollowCameraOptions): number {
  if (opts.arrived) return 16.2;

  const speed = opts.speedMps ?? 0;
  let zoom: number;
  if (speed < 1.5) zoom = 17.8;
  else if (speed < 6) zoom = 17.2;
  else if (speed < 12) zoom = 16.6;
  else zoom = 15.8;

  const dTurn = opts.distanceToTurnM;
  if (dTurn != null && Number.isFinite(dTurn) && dTurn < 80) {
    zoom += dTurn < 40 ? 0.9 : 0.55;
  }

  return Math.max(15.2, Math.min(18.2, zoom));
}

/**
 * Smooth follow camera with bottom-bias padding (set on map), heading rotation,
 * dynamic zoom, look-ahead, and arrived freeze.
 * Prefer setCenter/setHeading — reliable on Android/iOS mobile web.
 */
export function tickNavigationCamera(
  map: any,
  target: LatLng,
  targetHeading: number,
  state: CameraState,
  deltaMs: number,
  isMovingOrOpts: boolean | FollowCameraOptions = true
): void {
  if (!map || !target) return;

  const opts: FollowCameraOptions =
    typeof isMovingOrOpts === 'boolean'
      ? { isMoving: isMovingOrOpts }
      : isMovingOrOpts || {};

  const isMoving = opts.isMoving ?? true;
  const arrived = Boolean(opts.arrived);
  const accuracyM = opts.accuracyM;
  const poorGps = accuracyM != null && accuracyM > 45;

  const lookAhead = arrived ? 0 : opts.lookAheadM ?? (isMoving ? 22 : 8);
  const aim =
    lookAhead > 0 && Number.isFinite(targetHeading)
      ? offsetByHeading(target, targetHeading, lookAhead)
      : target;

  // Slower lerp when stopped or poor GPS
  let rate = isMoving ? 5.5 : 3.2;
  if (poorGps) rate *= 0.55;
  if (arrived) rate *= 0.4;
  const t = Math.min(1, (deltaMs / 1000) * rate);

  state.lat = lerp(state.lat, aim.lat, t);
  state.lng = lerp(state.lng, aim.lng, t);

  if (arrived) {
    state.tilt = lerp(state.tilt, 0, t * 0.7);
    // Freeze heading updates when arrived
  } else {
    const headingT = isMoving ? t : t * 0.25;
    if (isMoving || Math.abs(((targetHeading - state.heading + 540) % 360) - 180) > 8) {
      state.heading = lerpHeading(state.heading, targetHeading || state.heading, headingT);
    }
    state.tilt = lerp(state.tilt, 45, t * 0.55);
  }

  const targetZoom = computeNavZoom(opts);
  state.zoom = lerp(state.zoom, targetZoom, t * 0.45);

  const center = { lat: state.lat, lng: state.lng };

  // Prefer setCenter/panTo — heading/tilt often fail on mobile WebView raster maps
  try {
    if (typeof map.panTo === 'function') {
      map.panTo(center);
    } else if (typeof map.setCenter === 'function') {
      map.setCenter(center);
    }
    if (typeof map.setZoom === 'function') {
      map.setZoom(state.zoom);
    }
    if (!arrived && isMoving) {
      try {
        if (typeof map.setTilt === 'function') map.setTilt(state.tilt);
        if (typeof map.setHeading === 'function') map.setHeading(state.heading);
      } catch {
        /* heading/tilt unsupported — follow still works via panTo */
      }
    } else if (arrived) {
      try {
        if (typeof map.setTilt === 'function') map.setTilt(0);
      } catch {
        /* ignore */
      }
    }
  } catch (e) {
    console.warn('[camera] follow failed:', e);
    try {
      map.panTo?.(center);
    } catch {
      /* ignore */
    }
  }
}

/** Alias used by Go7StyleNavMap */
export const tickFollowCamera = tickNavigationCamera;
